import dayjs from 'dayjs';
import moment from 'moment-timezone';

export const convertToHourMinute = (date: string) => {
  return date ? dayjs(date).format('hh:mm A') : '';
};

export const remainingTime = (targetDate: string | Date, fromDate?: string | Date) => {
  const target = dayjs(targetDate);
  const now = fromDate ? dayjs(fromDate) : dayjs();
  const remaining = target.diff(now);

  if (remaining <= 0) {
    return 'The target time has been reached.';
  }

  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

  let output = '';

  if (days > 0) {
    output += `${days}d `;
  }

  output += `${hours}h `;
  output += `${minutes}m `;
  output += `${seconds}s`;

  return output;
};

interface DateFormatOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
  hour12?: boolean;
  timeZone?: string;
  second?: 'numeric' | '2-digit';
}

export function formatDateTime(date: string | Date | number, options: DateFormatOptions = {}): string {
  return new Intl.DateTimeFormat('en-US', { ...options }).format(new Date(date));
}

export function formatDateTimeWithEventLocale({
  date,
  locale,
  timezone,
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }
}: {
  date: string | Date | number;
  locale: string;
  timezone?: string;
  options?: DateFormatOptions;
}): string {
  const parsedDate = new Date(date);
  const formatterOptions = { ...options };
  if (timezone) {
    formatterOptions.timeZone = timezone;
  }
  const formatter = new Intl.DateTimeFormat(locale, formatterOptions);

  return formatter.format(parsedDate);
}

const TIMEZONE_ABBR_OVERRIDES: Record<string, string> = {
  'Asia/Dubai': 'GST',
  'Asia/Muscat': 'GST',
  'Asia/Riyadh': 'AST',
  'Asia/Qatar': 'AST',
  'Asia/Baghdad': 'AST'
};

export function getTimezoneAbbr(date: string, timezone?: string): string {
  let timeZone = timezone;
  if (!timezone) {
    timeZone = moment.tz.guess();
  }

  if (timeZone && TIMEZONE_ABBR_OVERRIDES[timeZone]) {
    return TIMEZONE_ABBR_OVERRIDES[timeZone];
  }

  const abbr = moment(date)
    .tz(timeZone ?? '')
    .format('z');

  // Check if the abbreviation is a number (like +05:30, -08:00, -05, +0545, -0930, etc.)
  // If it starts with + or - followed by numbers, add GMT prefix and standardize format
  if (/^[+-]\d{1,2}(:\d{2}|\d{2})?$/.test(abbr)) {
    let formattedAbbr = abbr;

    // If it's a 4-digit format without colon (like +0545, -0930), add colon
    if (/^[+-]\d{4}$/.test(abbr)) {
      formattedAbbr = abbr.slice(0, 3) + ':' + abbr.slice(3);
    }
    // If it's hour-only format (like +05, -08), add :00
    else if (/^[+-]\d{1,2}$/.test(abbr)) {
      formattedAbbr = abbr + ':00';
    }

    return `GMT${formattedAbbr}`;
  }

  return abbr;
}

export function createDateFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, options);
}

export function getFormattedDateInLocaleWithTimezone({
  date,
  locale,
  timezone,
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }
}: {
  date: string | Date | number;
  locale: string;
  timezone: string;
  options?: DateFormatOptions;
}): string {
  const formattedDate = formatDateTimeWithEventLocale({ date, locale, timezone, options });
  const timezoneAbbr = getTimezoneAbbr(date.toString(), timezone);
  return `${formattedDate} ${timezoneAbbr}`;
}
